import { CloseIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Stack,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";

type CustomFilterItem = {
  value: string;
  label: string;
  filter: string;
  options?: { label: string; value: string; type?: string }[];
  multipleSelect?: boolean;
  searchable?: boolean;
};

export type CustomSearchProps = {
  getFilters: () => Promise<{
    filters: CustomFilterItem[];
    existingFilters: CustomFilterItem[];
  }>;
  baseUrl: string;
};

type CustomFilterValue = CustomFilterItem & { inputValue?: string };

export const CustomSearch = ({ getFilters, baseUrl }: CustomSearchProps) => {
  const [filters, setFilters] = useState<CustomFilterItem[]>([]);
  const [selectedFilters, setSelectedFilters] = useState<CustomFilterValue[]>(
    []
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [filterValue, setFilterValue] = useState("");
  const [loading, setLoading] = useState(true);
  const [btnDisabled, setBtnDisabled] = useState(true);
  const [error, setError] = useState("");
  const [inputError, setInputError] = useState("");

  useEffect(() => {
    let subscribed = true;
    (async () => {
      try {
        const { filters, existingFilters } = await getFilters();
        if (subscribed) {
          setError("");
          setFilters(filters);
          setSelectedFilters(existingFilters);
        }
      } catch (err) {
        setError("An error occurred");
      }
      setLoading(false);
    })();

    return () => {
      subscribed = false;
    };
  }, []);

  const filterExistsInBoard = (
    selectedFilter: CustomFilterValue,
    filters: CustomFilterValue[]
  ) => {
    const filterExists = filters.find(
      (filter) => filter.value === selectedFilter.value
    );
    if (filterExists) return filters;

    return [...filters, selectedFilter];
  };

  const handleFilterChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    if (event.target.value === "") return;

    setFilterValue(event.target.value);
    const selectedFilter = filters.find(
      (filter) => filter.value === event.target.value
    );
    if (selectedFilter) {
      setSelectedFilters((filters) =>
        filterExistsInBoard(selectedFilter, filters)
      );
    }
  };

  const handleRemovingFilter = (filterValue: string) => {
    setSelectedFilters((filters) =>
      filters.filter((filter) => {
        if (filter.value === filterValue) {
          filter.inputValue = "";
          return null;
        }
        return filter;
      })
    );
  };

  const handleInputChange = (event: any, index: number) => {
    setSelectedFilters((filters) => {
      const newFilters = [...filters];
      newFilters[index].inputValue = event.target.value;
      return newFilters;
    });
  };

  const generateKeyValuePair = (filter: CustomFilterValue) => {
    let key = filter.value;
    let value = filter.inputValue;
    if (filter.value.includes("_booking") && filter.inputValue) {
      const inputValues = filter.inputValue.split("_");
      key = `${filter.value}_${inputValues[1]}`;
      value = inputValues[0];
    }
    return { key, value };
  };

  const handleSubmit = () => {
    let url = "";
    if (selectedFilters.length <= 0) {
      return setInputError("No filters selected");
    }

    selectedFilters.forEach((filter) => {
      let { key, value } = generateKeyValuePair(filter);
      url += `${key}:${value}$$`;
    });
    url = url.replaceAll('&', '%26');
    const urlQueries = window.location.search.split("&");
    const queries = urlQueries.map(query => decodeURIComponent(query));
    setBtnDisabled(true);
    if (queries.length > 0) {
      const djangoFilters = queries
        .filter((query) => !query.includes("q="))
        .map((query) => query.substring(query.indexOf("?") + 1));
      return (window.location.href =
        djangoFilters.length > 0
          ? `${baseUrl}?${djangoFilters.join("&")}&q=${url}`
          : `${baseUrl}?q=${url}`);
    }
    return (window.location.href = `${baseUrl}?q=${url}`);
  };

  const clearField = () => {
    const queries = decodeURIComponent(window.location.search).split("&");
    if (queries.length > 0) {
      const djangoFilters = queries
        .filter((query) => !query.includes("q="))
        .map((query) => query.substring(query.indexOf("?") + 1));
      return (window.location.href =
        djangoFilters.length > 0
          ? `${baseUrl}?${djangoFilters.join("&")}`
          : `${baseUrl}`);
    }
    return (window.location.href = baseUrl);
  };

  useEffect(() => {
    if (selectedFilters.length <= 0) {
      setBtnDisabled(true);
    } else {
      setBtnDisabled(false);
    }
  }, [selectedFilters]);

  return (
    <Box>
      <Button
        onClick={() => setModalOpen(true)}
        variant="solid"
        colorScheme={"blackAlpha"}
      >
        Custom Search
      </Button>
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        trapFocus={true}
      >
        <ModalOverlay />
        <ModalContent minW={"800px"} w="fit-content" py="1rem">
          <ModalCloseButton mt="1rem" />
          <ModalHeader>
            <Heading size="lg" mb="1rem">
              Custom Search
            </Heading>
          </ModalHeader>
          <ModalBody>
            {loading ? (
              <Box
                display="flex"
                justifyContent={"center"}
                alignItems={"center"}
              >
                <Spinner
                  thickness="4px"
                  speed="0.65s"
                  emptyColor="gray.200"
                  color="blue.500"
                  size="xl"
                />
              </Box>
            ) : error ? (
              <Text color={"tomato"}>
                {error}. Please try reloading the page
              </Text>
            ) : (
              <>
                <Stack spacing={[3, 4]} fontSize="sm" isInline mb="1.5rem">
                  <FormControl mb="1rem">
                    <FormLabel htmlFor="requests">Add filter</FormLabel>
                    <Select
                      placeholder="Add filter"
                      onChange={handleFilterChange}
                      value={filterValue}
                    >
                      {filters.map((filter, index) => (
                        <option key={index} value={filter.value}>
                          {filter.label}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
                {inputError && <Text color={"tomato"}>{error}</Text>}
                <Box>
                  {selectedFilters.map((filter, index) => (
                    <Box
                      display={"flex"}
                      key={`${filter.label}-${filter.value}-${index}`}
                      alignItems={"center"}
                      columnGap="4"
                      mb="5"
                    >
                      <Button
                        width={"fit-content"}
                        fontSize="sm"
                        variant={"solid"}
                        leftIcon={<CloseIcon w="2" h="2" />}
                        onClick={() => handleRemovingFilter(filter.value)}
                        fontWeight="normal"
                      >
                        {filter.label}
                      </Button>
                      <Text width={"fit-content"} display="block">
                        {filter.filter}
                      </Text>
                      {!filter.options ? (
                        <>
                          <Input
                            placeholder="Enter value"
                            fontSize="sm"
                            width={"fit-content"}
                            value={selectedFilters[index].inputValue || ""}
                            onChange={(e) => handleInputChange(e, index)}
                          />
                          {filter.value === "generic" && (
                            <Text fontSize={"xs"} maxW="250px">
                              (in tutor description - [description, track
                              record, schools taught], education course, work
                              experience, certification)
                            </Text>
                          )}
                        </>
                      ) : filter.multipleSelect ? (
                           <MultiSelectInput
                             handleChange={(values: string[]) => {
                               const newFilters = [...selectedFilters];
                               newFilters[index].inputValue = values.join(",");
                               setSelectedFilters((filters) => {
                                 return newFilters;
                               });
                             }}
                             filter={filter}
                           />
                      ) : (
                        <Select
                          placeholder="Select option"
                          fontSize="sm"
                          width={"fit-content"}
                          value={selectedFilters[index].inputValue}
                          onChange={(e) => handleInputChange(e, index)}
                        >
                          {filter.options.map((option, index) => (
                            <option
                              key={index}
                              value={
                                filter.value.includes("_booking")
                                  ? `${option.value}_${option.type}`
                                  : option.value
                              }
                            >
                              {option.label}
                            </option>
                          ))}
                        </Select>
                      )}
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </ModalBody>
          <ModalFooter
            display={"flex"}
            justifyContent="space-between"
            alignItems={"center"}
          >
            <Button
              colorScheme={"blue"}
              type="submit"
              onClick={handleSubmit}
              disabled={Boolean(error) || btnDisabled}
            >
              Apply
            </Button>
            <Button
              colorScheme={"blackAlpha"}
              type="submit"
              onClick={clearField}
            >
              Clear field
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

const MultiSelectInput = ({
  handleChange,
  filter,
}: {
  handleChange: (values: string[]) => void;
  filter: CustomFilterValue;
}) => {
  const { label: filterLabel, options: items, searchable } = filter;
  const [inputValue, setInputValue] = useState("");
  const [selectedItems, setSelectedItems] = useState<string[]>(
    filter.inputValue ? filter.inputValue.split(",") : []
  );

  const handleSelectChange = (event: any) => {
    if (event.target.value === "") return;

    setSelectedItems((_selectedItems) =>
      Array.from(new Set([..._selectedItems, event.target.value]))
    );
  };

  const handleInputChange = (event: any) => {
    event.preventDefault();

    if (!searchable) return;

    setSelectedItems((_selectedItems) =>
      Array.from(new Set([..._selectedItems, inputValue]))
    );
    setInputValue("");
  };

  const removeItem = (itemToRemove: string) => {
    setSelectedItems((_selectedItems) =>
      _selectedItems.filter((item) => item !== itemToRemove)
    );
  };

  useEffect(() => {
    handleChange(selectedItems);
  }, [selectedItems]);

  return (
    <Box
      as="form"
      onSubmit={handleInputChange}
      display="grid"
      gridTemplateColumns={selectedItems.length > 0 ? "200px auto" : "1fr"}
      alignItems={"center"}
    >
      {searchable ? (
        <Input
          placeholder="Enter value"
          fontSize="sm"
          width={"fit-content"}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
        />
      ) : (
        <Select
          placeholder={`Select ${filterLabel}`}
          onChange={handleSelectChange}
          // value={selectValue}
          width={"fit-content"}
          maxW={"fit-content"}
          fontSize="sm"
        >
          {items?.map((option, index) => (
            <option key={index} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      )}
      <Box maxW="400px" maxH="100px" overflowY="scroll">
        {selectedItems.length > 0 && (
          <Box ml="1rem">
            {selectedItems.map((item, index) => (
              <Tag
                key={index}
                mr={1}
                mb={1}
                variant="solid"
                colorScheme="blackAlpha"
              >
                <TagLabel>{item}</TagLabel>
                <TagCloseButton onClick={() => removeItem(item)} />
              </Tag>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};
